import { IFocusZone } from "@fluentui/react/lib/FocusZone";
import {
  useCallback, useEffect, useRef, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { projectsFilterExpressionAdd, projectsFilterExpressionRemove } from "Store/Actions";
import { selectFilterExpression } from "Store/Selectors/Filter";
import { FilterExpression } from "Utils/Filter";
import { isNullOrWhiteSpace } from "Utils/String";
import {
  extractInjectedToken, hasInjectedToken, parseExpression, sanitizeSearch
} from "Utils/Tokenizr";
import { IUseSearchProps } from "./IUseSearchProps";

export function useSearch(): IUseSearchProps {
  const dispatch = useDispatch();
  const expression = useSelector(selectFilterExpression);

  const searchRef = useRef<HTMLDivElement>(null);
  const focusZoneRef = useRef<IFocusZone>(null);

  const [searchTerm, setSearchTerm] = useState(``);
  const [searchHighlight, setSearchHighlight] = useState<string>(null);
  const [isCalloutFocused, setIsCalloutFocused] = useState(false);
  const [isCallOutVisible, setIsCallOutVisible] = useState(false);
  const [focusBackToSearchbox, setFocusBackToSearchbox] = useState(false);
  const [filterExpression, setFilterExpression] = useState<FilterExpression>(null);

  useEffect(() => {
    if (isNullOrWhiteSpace(expression)) {
      setFilterExpression(null);
      return;
    }

    if (hasInjectedToken(expression)) {
      setSearchTerm(extractInjectedToken(expression));
      searchRef.current.focus();
    }

    setFilterExpression(parseExpression(expression));
  }, [expression, searchRef, setSearchTerm]);

  const onChange = useCallback(
    (_, newValue?: string) => {
      if (isNullOrWhiteSpace(newValue)) {
        setSearchTerm(``);
        dispatch(projectsFilterExpressionAdd(``));
        return;
      }

      setSearchTerm(newValue);
    },
    [dispatch]
  );

  const onAdd = useCallback(() => {
    dispatch(projectsFilterExpressionAdd(searchTerm));
    setSearchTerm(``);
  }, [dispatch, searchTerm, setSearchTerm]);

  const onRemove = useCallback(
    (fieldName: string, search: string) => {
      dispatch(projectsFilterExpressionRemove(fieldName, search));
    },
    [dispatch]
  );

  const onEdit = useCallback(
    (fieldName: string, search: string) => {
      onRemove(fieldName, search);
      setSearchTerm(`${fieldName !== `*` ? `${fieldName}:` : ``}${sanitizeSearch(search)}`);
    },
    [onRemove, setSearchTerm]
  );

  const onDismiss = useCallback(() => {
    setIsCallOutVisible(false);
  }, []);

  return {
    focusBackToSearchbox,
    focusZoneRef,
    isCallOutVisible,
    isCalloutFocused,
    onAdd,
    onChange,
    onDismiss,
    onEdit,
    onRemove,
    searchHighlight,
    searchRef,
    searchTerm,
    setFocusBackToSearchbox,
    setIsCallOutVisible,
    setIsCalloutFocused,
    setSearchHighlight,
    setSearchTerm,
    filterExpression
  };
}
